/**
 * DisputeStatusBadge - Visual status indicator for a dispute
 *
 * DevStory 4.5: Muestra el estado del ciclo de vida de la disputa:
 * OPEN -> PUBLIC -> PENDING_DAO_RESOLUTION -> RESOLVED / REJECTED / CANCELLED
 */

import { cn } from '@lib/cn';
import type { DisputeStatus } from '@/types';

interface DisputeStatusBadgeProps {
  status?: DisputeStatus;
  customLabel?: string;
  variant?: 'disputed' | 'waiting' | 'default';
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const STATUS_CONFIG: Record<
  DisputeStatus,
  { label: string; bg: string; text: string; border: string; dot: string; icon: string }
> = {
  OPEN: {
    label: 'Open',
    bg: 'bg-amber-500/10',
    text: 'text-amber-400',
    border: 'border-amber-500/30',
    dot: 'bg-amber-400',
    icon: 'ri-time-line',
  },
  PUBLIC: {
    label: 'Public Dispute',
    bg: 'bg-blue-500/10',
    text: 'text-blue-400',
    border: 'border-blue-500/30',
    dot: 'bg-blue-400',
    icon: 'ri-global-line',
  },
  PENDING_DAO_RESOLUTION: {
    label: 'Pending DAO Resolution',
    bg: 'bg-purple-500/10',
    text: 'text-purple-400',
    border: 'border-purple-500/30',
    dot: 'bg-purple-400',
    icon: 'ri-scales-3-line',
  },
  RESOLVED: {
    label: 'Dispute Closed, Verdict Emitted',
    bg: 'bg-emerald-500/15',
    text: 'text-emerald-400',
    border: 'border-emerald-500/30',
    dot: 'bg-emerald-400',
    icon: 'ri-checkbox-circle-line',
  },
  REJECTED: {
    label: 'Dispute Closed, Rejected',
    bg: 'bg-rose-500/15',
    text: 'text-rose-400',
    border: 'border-rose-500/30',
    dot: 'bg-rose-400',
    icon: 'ri-close-circle-line',
  },
  CANCELLED: {
    label: 'Cancelled',
    bg: 'bg-gray-500/10',
    text: 'text-gray-400',
    border: 'border-gray-500/30',
    dot: 'bg-gray-400',
    icon: 'ri-prohibited-line',
  },
};

export function DisputeStatusBadge({
  status,
  customLabel,
  variant,
  className,
  size = 'md',
}: DisputeStatusBadgeProps): JSX.Element {
  let config = (status ? STATUS_CONFIG[status] : undefined) ?? {
    label: customLabel || 'Disputed',
    bg: 'bg-gray-500/10',
    text: 'text-gray-400',
    border: 'border-gray-500/30',
    dot: 'bg-gray-400',
    icon: 'ri-information-line',
  };

  if (variant === 'disputed' || (!status && customLabel === 'Disputed')) {
    config = {
      label: customLabel || 'Disputed',
      bg: 'bg-[#461A1A]',
      text: 'text-[#ff7575]',
      border: 'border-[#6B1919]',
      dot: 'bg-[#ff7575]',
      icon: 'ri-scales-3-line',
    };
  } else if (variant === 'waiting') {
    config = {
      label: customLabel || 'Waiting',
      bg: 'bg-amber-500/10',
      text: 'text-amber-300',
      border: 'border-amber-500/30',
      dot: 'bg-amber-300',
      icon: 'ri-time-line',
    };
  }

  const label = customLabel || config.label;

  const sizeClasses = {
    sm: 'text-[11px] px-2 py-0.5 gap-1',
    md: 'text-xs px-2.5 py-1 gap-1.5',
    lg: 'text-sm px-3 py-1.5 gap-2',
  }[size];

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full border',
        config.bg,
        config.text,
        config.border,
        sizeClasses,
        className
      )}
    >
      <i className={cn(config.icon, 'text-current text-[1.1em]')} />
      <span>{label}</span>
    </span>
  );
}
